import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Eye, Pencil, Plus, Trash2, UserPlus } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import { imgUrl } from "@/lib/content";
import { Chips, Empty, ImageField, Loading, PageHead, useAdmin } from "./ui";

const NEW = { kind: "signup", image_mobile: "", link_url: "", start_date: "", end_date: "", name: "", list_name: "Subscribers", title: "", body: "", button_label: "Sign me up", success_message: "Thank you!",
  collect_birthday: false, trigger: "site_time", seconds: 30, pages: "all", frequency_days: 7, image: "", active: true };
const KEYS = Object.keys(NEW);

const F = ({ label, hint, children }) => <div className="field"><label>{label}</label>{hint && <p className="ad-hint">{hint}</p>}{children}</div>;

const PopupForm = ({ initial, onSaved }) => {
  const [p, setP] = useState(() => Object.fromEntries(KEYS.map((k) => [k, initial[k] ?? NEW[k]])));
  const [busy, setBusy] = useState(false);
  const isImage = p.kind === "image";
  const set = (k) => (e) => setP({ ...p, [k]: e?.target ? (e.target.type === "number" ? Number(e.target.value) : e.target.value) : e });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (isImage && !p.image) { toast.error("Add a promotion picture"); return; }
      if (initial.id) await api.put(`/admin/popups/${initial.id}`, p);
      else await api.post("/admin/popups", p);
      toast.success("Pop-up saved");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <form className="form ad-popup-form" onSubmit={save} data-testid="popup-editor-form">
      <F label="Pop-up type">
        <Chips options={[["signup", "Sign-up form"], ["image", "Picture only (promotion)"]]} value={p.kind} onChange={set("kind")} testPrefix="popup-edit-kind" />
      </F>
      <F label="Internal name"><input required value={p.name} onChange={set("name")} data-testid="popup-edit-name" /></F>
      {isImage ? (
        <>
          <F label="Promotion picture" hint="Shown on computers and tablets — and on phones if no phone picture is added"><ImageField value={p.image} onChange={set("image")} testId="popup-edit-image" /></F>
          <F label="Phone picture (optional)" hint="Portrait picture for phones, e.g. 1080 × 1350"><ImageField value={p.image_mobile} onChange={set("image_mobile")} testId="popup-edit-image-mobile" /></F>
          <F label="Link when tapped (optional)" hint="#book, /shop, /our-work or a full https:// link. Leave empty for picture only"><input value={p.link_url} onChange={set("link_url")} placeholder="#book" data-testid="popup-edit-link" /></F>
        </>
      ) : (
        <>
          <F label="List name" hint="Sign-ups are saved to this list (e.g. Birthday Club)"><input required value={p.list_name} onChange={set("list_name")} data-testid="popup-edit-list" /></F>
          <F label="Headline"><input required value={p.title} onChange={set("title")} data-testid="popup-edit-title" /></F>
          <F label="Message"><textarea value={p.body} onChange={set("body")} data-testid="popup-edit-body" /></F>
          <div className="field-row">
            <F label="Button label"><input value={p.button_label} onChange={set("button_label")} data-testid="popup-edit-button" /></F>
            <F label="Thank-you message"><input value={p.success_message} onChange={set("success_message")} data-testid="popup-edit-success" /></F>
          </div>
        </>
      )}
      <div className="field-row">
        <F label="Start date (optional)"><input type="date" value={p.start_date} max={p.end_date || undefined} onChange={set("start_date")} data-testid="popup-edit-start" /></F>
        <F label="End date (optional)" hint="Turns off automatically after this day"><input type="date" value={p.end_date} min={p.start_date || undefined} onChange={set("end_date")} data-testid="popup-edit-end" /></F>
      </div>
      <div className="field-row">
        <F label="Show after">
          <select value={p.trigger} onChange={set("trigger")} data-testid="popup-edit-trigger">
            <option value="site_time">Time on the site (whole visit)</option><option value="page_time">Time on the current page</option>
          </select>
        </F>
        <F label="Seconds"><input type="number" min="0" max="3600" value={p.seconds} onChange={set("seconds")} data-testid="popup-edit-seconds" /></F>
      </div>
      <div className="field-row">
        <F label="Pages" hint='"all", or comma list like /, /inspiration/*'><input value={p.pages} onChange={set("pages")} data-testid="popup-edit-pages" /></F>
        <F label="Show again after (days)" hint="0 = once per visit"><input type="number" min="0" max="365" value={p.frequency_days} onChange={set("frequency_days")} data-testid="popup-edit-frequency" /></F>
      </div>
      {!isImage && <F label="Image (optional)"><ImageField value={p.image} onChange={set("image")} testId="popup-edit-image" /></F>}
      <div className="ad-switches">
        {!isImage && <label className="ad-check"><Switch checked={p.collect_birthday} onCheckedChange={set("collect_birthday")} data-testid="popup-edit-birthday" /> Ask for birthday</label>}
        <label className="ad-check"><Switch checked={p.active} onCheckedChange={set("active")} data-testid="popup-edit-active" /> Active on site</label>
      </div>
      <button className="btn gold" type="submit" disabled={busy} data-testid="popup-edit-save">Save pop-up</button>
    </form>
  );
};

const PopupCard = ({ p, onEdit, refresh }) => {
  const toggle = async (active) => {
    try { await api.put(`/admin/popups/${p.id}`, { ...Object.fromEntries(KEYS.map((k) => [k, p[k]])), active }); refresh(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${p.name}"? Sign-ups already collected are kept.`)) return;
    await api.delete(`/admin/popups/${p.id}`);
    refresh();
  };
  const rate = p.views ? Math.round(((p.conversions || 0) / p.views) * 100) : 0;
  return (
    <article className="ad-card ad-popup" data-testid={`popup-card-${p.id}`}>
      <div className="req-top">
        <div><h3>{p.name} <span className="pill" data-testid="popup-card-kind">{p.kind === "image" ? "Picture" : "Sign-up"}</span></h3><p className="req-meta">{p.kind === "image" ? `Link: ${p.link_url || "none"}` : `List: ${p.list_name}`} · {p.start_date || p.end_date ? `${p.start_date || "now"} → ${p.end_date || "no end"} · ` : ""}{p.trigger === "site_time" ? "visit" : "page"} time {p.seconds}s · pages: {p.pages}</p></div>
        <Switch checked={p.active} onCheckedChange={toggle} data-testid="popup-card-active-switch" />
      </div>
      {p.kind === "image" ? <img className="ad-popup-thumb" src={imgUrl(p.image, 600)} alt="" /> : <p className="ad-popup-title">“{p.title}”</p>}
      <div className="ad-popup-stats">
        <span><Eye size={14} /> {p.views || 0} views</span>{p.kind === "image" ? <span><UserPlus size={14} /> {p.conversions || 0} taps</span> : <span><UserPlus size={14} /> {p.subscribers} sign-ups</span>}<span>{rate}% conversion</span>
      </div>
      <div className="req-actions">
        <button className="btn light" onClick={onEdit} data-testid="popup-card-edit-button"><Pencil size={14} /> Edit</button>
        <button className="btn light ad-danger" onClick={remove} data-testid="popup-card-delete-button"><Trash2 size={14} /> Delete</button>
      </div>
    </article>
  );
};

export default function Popups() {
  const { data } = useAdmin("popups", "/admin/popups");
  const [editing, setEditing] = useState(null);
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin", "popups"] });
  return (
    <div data-testid="admin-popups">
      <PageHead eyebrow="Grow your lists" title="Pop-ups">
        <button className="btn gold" onClick={() => setEditing({})} data-testid="popups-new-button"><Plus size={14} /> New pop-up</button>
      </PageHead>
      {!data ? <Loading /> : data.length === 0 ? <Empty>No pop-ups yet.</Empty> : (
        <div className="ad-grid2">{data.map((p) => <PopupCard key={p.id} p={p} onEdit={() => setEditing(p)} refresh={refresh} />)}</div>
      )}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="ad-dialog" data-testid="popup-editor-dialog">
          <DialogTitle className="ad-msg-title">{editing?.id ? "Edit pop-up" : "New pop-up"}</DialogTitle>
          {editing && <PopupForm initial={editing} onSaved={() => { setEditing(null); refresh(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
